import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { homeApi } from '../api/home';
import BottomNav from '../components/BottomNav';
import Navbar from '../components/Navbar';
import './Library.css';

const Library = () => {
  const navigate = useNavigate();
  const [currentCategory, setCurrentCategory] = useState(0);
  const [categoryList, setCategoryList] = useState([{ id: 0, name: '推荐' }]);
  const [articleList, setArticleList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isCategoryLoaded, setIsCategoryLoaded] = useState(false);
  
  const contentRef = useRef(null);
  const categoryScrollRef = useRef(null);
  const categoryItemRefs = useRef([]);

  useEffect(() => {
    initData();
  }, []);

  const initData = async () => {
    try {
      setLoading(true);
      await getCategories();
      await getArticleList(1);
      setLoading(false);
    } catch (error) {
      console.error('初始化数据失败:', error);
      setLoading(false);
    }
  };

  const getCategories = async () => {
    try {
      const result = await homeApi.getCategories();
      if (result.code === 200 || result.code === 1) {
        setCategoryList(result.data || [{ id: 0, name: '推荐' }]);
        setIsCategoryLoaded(true);
      }
    } catch (error) {
      console.error('获取分类失败:', error);
      setIsCategoryLoaded(true);
    }
  };

  const getArticleList = async (pageNum, categoryIdParam = null) => {
    try {
      if (pageNum === 1) {
        setArticleList([]);
      }
      
      if (loadingMore && !hasMore) {
        return;
      }

      // 使用传入的categoryId参数，如果没有则使用当前选中的分类ID
      const categoryId = categoryIdParam !== null ? categoryIdParam : (categoryList[currentCategory]?.id || 0);
      
      const result = await homeApi.getArticleList({ 
        category_id: categoryId, 
        page: pageNum,
        limit: 10
      });
      
      if (result.code === 200 || result.code === 1) {
        const list = result.data.list || [];
        const total = result.data.total || 0;
        const pageSize = result.data.limit || 10;
      
        if (pageNum === 1) {
          setArticleList(list);
        } else {
          setArticleList(prev => [...prev, ...list]);
        }
      
        setHasMore((pageNum * pageSize) < total);
        setPage(pageNum);
      }
      
    } catch (error) {
      console.error('获取文章列表失败:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMore(false);
    }
  };

  const handleCategoryChange = (index) => {
    if (currentCategory === index) return;
    
    setCurrentCategory(index);
    setPage(1);
    setHasMore(true);
    setArticleList([]);
    setLoading(true);
    
    // 直接使用新的分类ID，而不是依赖异步更新的currentCategory状态
    const newCategoryId = categoryList[index]?.id || 0;
    getArticleList(1, newCategoryId);
    
    // 将激活的标签滚动到中间位置
    setTimeout(() => {
      const activeItem = categoryItemRefs.current[index];
      const scrollContainer = categoryScrollRef.current;
      
      if (activeItem && scrollContainer) {
        const containerWidth = scrollContainer.clientWidth;
        const itemLeft = activeItem.offsetLeft;
        const itemWidth = activeItem.clientWidth;
        const scrollLeft = itemLeft - (containerWidth / 2) + (itemWidth / 2);
        
        scrollContainer.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setPage(1);
    setHasMore(true);
    await getArticleList(1);
  };

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    await getArticleList(page + 1);
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 100 && hasMore && !loadingMore) {
      handleLoadMore();
    }
  };

  const viewArticle = (id) => {
    navigate(`/library/article_detail/${id}`);
  };

  const navigateToSearch = () => {
    navigate('/homePages/search');
  };

  if (loading) {
    return <div className="loading">加载中...</div>;
  }

  return (
    <div className="library-container">
      <Navbar title="资料库" />
      
      <div className="category-tabs">
        <div className="category-scroll" ref={categoryScrollRef}>
          <div className="category-list">
            {categoryList.map((category, index) => (
              <div
                key={category.id}
                ref={el => categoryItemRefs.current[index] = el}
                className={`category-item ${currentCategory === index ? 'active' : ''}`}
                onClick={() => handleCategoryChange(index)}
              >
                {category.name}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div 
        className="content-area"
        ref={contentRef}
        onScroll={handleScroll}
      >
        {/* 下拉刷新提示 */}
        {refreshing && (
          <div className="refresh-indicator">
            <span>刷新中...</span>
          </div>
        )}

        {/* 文章列表 */}
        <div className="article-list">
          {articleList.map((item, index) => (
          <div key={item.id} className="library-item" onClick={() => viewArticle(item.id)}>
            <div className="library-content">
              <div className="library-info">
                <div className="library-title">{item.title}</div>
                <div className="library-meta">
                  <span>{item.category_name}</span>
                  <span>·</span>
                  <span>👁️ {item.view_num || 0}</span>
                </div>
              </div>
              {item.logo && (
                <div className="library-logo" style={{ backgroundImage: `url(${item.logo})` }}></div>
              )}
            </div>
            {index < articleList.length - 1 && <div className="divider"></div>}
          </div>
        ))}

          {/* 空状态 */}
          {articleList.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">📄</div>
              <div className="empty-text">暂无文章</div>
            </div>
          )}

          {/* 加载更多提示 */}
          {loadingMore && (
            <div className="loading-more">
              <span>加载中...</span>
            </div>
          )}

          {/* 没有更多数据提示 */}
          {!hasMore && articleList.length > 0 && (
            <div className="no-more">
              <span>没有更多数据了</span>
            </div>
          )}
        </div>
      </div>
    
    {/* 底部导航 */}
    <BottomNav activeTab="library" />
  </div>
);
};

export default Library;